import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileDown, Printer, Link2, Mail } from "lucide-react";
import { getTest, getAthlete } from "../lib/api";
import LactateChart from "../components/LactateChart";
import ZonesTable from "../components/ZonesTable";
import { BrandBanner, BrandFooter, formatLongDate as formatDate } from "../components/ReportFrame";
import { ZONE_BENEFITS } from "../lib/constants";
import EmailReportModal from "../components/EmailReportModal";
import { toast } from "sonner";

function ThresholdSummary({ label, subtitle, data, speedUnit, accent }) {
  return (
    <div className="border border-[var(--tir-border)] p-5" style={{ borderLeft: `4px solid ${accent}` }}>
      <div className="font-serif-display text-3xl leading-none">{label}</div>
      <div className="text-[11px] tracking-[0.22em] uppercase text-[var(--tir-muted)] mt-1">
        {subtitle}
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-4">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">HR</div>
          <div className="font-mono-tir text-base">{data?.hr != null && data.hr !== "" ? `${data.hr} bpm` : "—"}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">Speed</div>
          <div className="font-mono-tir text-base">
            {data?.speed != null && data.speed !== "" ? `${data.speed} ${speedUnit}` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">Pace</div>
          <div className="font-mono-tir text-base">{data?.pace || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">RPE</div>
          <div className="font-mono-tir text-base">
            {data?.rpe != null && data.rpe !== "" ? `${data.rpe} / 10` : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReportPreviewPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [test, setTest] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const page1Ref = useRef(null);
  const page2Ref = useRef(null);
  const page3Ref = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const t = await getTest(id);
        setTest(t);
        if (t?.athlete_id) {
          try {
            const a = await getAthlete(t.athlete_id);
            setAthlete(a);
          } catch {
            /* athlete fetch failure shouldn't block report preview */
          }
        }
      } catch {
        toast.error("Couldn't load test");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // If navigated with ?send=1, auto-open the email modal once data is ready
  useEffect(() => {
    if (!loading && test && athlete && searchParams.get("send") === "1") {
      setEmailOpen(true);
    }
  }, [loading, test, athlete, searchParams]);

  /**
   * Build the PDF and return both a Blob and a default filename.
   * If `download` is true, also triggers a save dialog (existing export-PDF behavior).
   */
  const buildPdf = async ({ download = false } = {}) => {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageWidth = 210;
    const pageHeight = 297;
    const pages = [page1Ref.current, page2Ref.current, page3Ref.current].filter(Boolean);
    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const img = canvas.toDataURL("image/png");
      const ratio = canvas.width / canvas.height;
      let w = pageWidth;
      let h = w / ratio;
      if (h > pageHeight) {
        h = pageHeight;
        w = h * ratio;
      }
      const x = (pageWidth - w) / 2;
      const y = (pageHeight - h) / 2;
      if (i > 0) pdf.addPage();
      pdf.addImage(img, "PNG", x, y, w, h, undefined, "FAST");
    }
    const filename = `lactate-report-${(test.athlete_name || "athlete").replace(/\s+/g, "_")}-${test.test_date}.pdf`;
    if (download) pdf.save(filename);
    return { blob: pdf.output("blob"), filename };
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      await buildPdf({ download: true });
      toast.success("PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const closeEmail = () => {
    setEmailOpen(false);
    if (searchParams.get("send")) {
      searchParams.delete("send");
      setSearchParams(searchParams, { replace: true });
    }
  };

  if (loading || !test) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="report-preview-page" className="min-h-screen pb-20">
      {/* Toolbar (not printed) */}
      <div className="no-print sticky top-0 z-40 bg-[var(--tir-ivory)]/95 backdrop-blur border-b border-[var(--tir-border)]">
        <div className="max-w-[900px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link
            to={`/tests/${id}/edit`}
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors"
            data-testid="back-to-editor"
          >
            <ArrowLeft size={14} /> Back to editor
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                  toast.success("Report link copied");
                } catch {
                  toast.error("Couldn't copy. Long-press the URL bar instead");
                }
              }}
              data-testid="copy-link-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
            >
              <Link2 size={14} /> Copy link
            </button>
            <button
              onClick={() => window.print()}
              data-testid="print-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
            >
              <Printer size={14} /> Print
            </button>
            <button
              onClick={exportPDF}
              disabled={exporting}
              data-testid="export-pdf-btn"
              className="inline-flex items-center gap-2 border border-[var(--tir-ink)] bg-white px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors disabled:opacity-50"
            >
              <FileDown size={14} /> {exporting ? "Exporting…" : "Download"}
            </button>
            <button
              onClick={() => setEmailOpen(true)}
              data-testid="email-report-btn"
              className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors"
            >
              <Mail size={14} /> Send to athlete
            </button>
          </div>
        </div>
      </div>

      <EmailReportModal
        open={emailOpen}
        onClose={closeEmail}
        athlete={athlete || { id: test?.athlete_id, name: test?.athlete_name, email: "" }}
        testId={test?.id}
        testDate={test?.test_date}
        defaultFilename={`lactate-report-${(test?.athlete_name || "athlete").replace(/\s+/g, "_")}-${test?.test_date}.pdf`}
        generatePdf={async () => (await buildPdf()).blob}
        reportTypeLabel="test"
      />

      <div className="pt-8 px-4">
        {/* PAGE 1 */}
        <div ref={page1Ref} className="print-page" data-testid="report-page-1">
          <BrandBanner athleteName={test.athlete_name} testDate={test.test_date} />
          <div className="px-10 pt-8 pb-20">
            <div className="grid grid-cols-3 gap-6 border-b border-[var(--tir-border)] pb-6 mb-6">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">
                  Test Date
                </div>
                <div className="font-mono-tir text-sm mt-1">{formatDate(test.test_date)}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--tir-muted)]">
                  Protocol
                </div>
                <div className="font-mono-tir text-sm mt-1">
                  {test.protocol} · Speed unit {test.speed_unit.toUpperCase()}
                </div>
              </div>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2">
                Your Test Curve
              </div>
              <div className="font-serif-display text-3xl text-[var(--tir-ink)] mb-5 leading-tight">
                Blood Lactate &amp; Heart Rate vs Speed
              </div>
              <div className="bg-white">
                <LactateChart test={test} height={400} />
              </div>
            </div>

            <div className="mt-10">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[var(--tir-burgundy)] font-semibold mb-3">
                Your Threshold Results
              </div>
              <div className="grid grid-cols-2 gap-5">
                <ThresholdSummary
                  label="LT1"
                  subtitle="Aerobic Threshold"
                  data={test.lt1}
                  speedUnit={test.speed_unit}
                  accent="#1C1917"
                />
                <ThresholdSummary
                  label="LT2"
                  subtitle="Anaerobic Threshold"
                  data={test.lt2}
                  speedUnit={test.speed_unit}
                  accent="#7A1F2B"
                />
              </div>
            </div>

            {test.coach_note && (
              <div className="mt-10 border-t border-[var(--tir-border)] pt-6">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2">
                  Coach's Note
                </div>
                <p className="text-sm leading-relaxed text-[var(--tir-ink)] whitespace-pre-wrap">
                  {test.coach_note}
                </p>
              </div>
            )}
          </div>
          <BrandFooter />
        </div>

        {/* PAGE 2 */}
        <div ref={page2Ref} className="print-page" data-testid="report-page-2">
          <BrandBanner athleteName={test.athlete_name} testDate={test.test_date} />
          <div className="px-10 pt-8 pb-20">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2">
              Prescribed Training Zones
            </div>
            <div className="font-serif-display text-3xl text-[var(--tir-ink)] mb-6 leading-tight">
              Train with intent in every zone
            </div>
            <p className="text-sm text-[var(--tir-muted)] leading-relaxed mb-6 max-w-2xl">
              Your training zones are anchored to your LT1 and LT2 results. Use them as targets for
              heart rate, treadmill speed, or outdoor pace.
            </p>
            <ZonesTable zones={test.zones || []} speedUnit={test.speed_unit} readOnly />

            <div className="grid grid-cols-2 gap-6 mt-10">
              <div className="border-t-2 border-[var(--tir-ink)] pt-4">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[var(--tir-burgundy)] font-semibold">
                  Flatten the graph
                </div>
                <p className="text-sm text-[var(--tir-ink)] mt-2 leading-relaxed">
                  Volume in Zone 2 improves aerobic efficiency, so the lactate curve stays lower
                  for longer at the same speeds.
                </p>
              </div>
              <div className="border-t-2 border-[var(--tir-ink)] pt-4">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[var(--tir-burgundy)] font-semibold">
                  Shift it rightwards
                </div>
                <p className="text-sm text-[var(--tir-ink)] mt-2 leading-relaxed">
                  Targeted Zone 4 and 5 work pushes LT2 to a higher speed, meaning you can sustain
                  harder paces before lactate spikes.
                </p>
              </div>
            </div>
          </div>
          <BrandFooter />
        </div>

        {/* PAGE 3 */}
        <div ref={page3Ref} className="print-page" data-testid="report-page-3">
          <BrandBanner athleteName={test.athlete_name} testDate={test.test_date} />
          <div className="px-10 pt-8 pb-20">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[var(--tir-burgundy)] font-semibold mb-2">
              Your Zone Guide
            </div>
            <div className="font-serif-display text-3xl text-[var(--tir-ink)] mb-5 leading-tight">
              What each zone does, and how to use it
            </div>
            <div className="grid grid-cols-1 gap-3">
              {(test.zone_guidance && test.zone_guidance.length === 5
                ? test.zone_guidance
                : ZONE_BENEFITS
              ).map((z, idx) => (
                <div key={idx} className="border border-[var(--tir-border)] p-4">
                  <div className="font-serif-display text-lg text-[var(--tir-ink)] leading-tight mb-2">
                    {z.label}
                  </div>
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-5">
                      <div className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1.5">
                        Benefits
                      </div>
                      <ul className="space-y-1">
                        {z.points.map((p, i) => (
                          <li
                            key={i}
                            className="text-[11.5px] text-[var(--tir-ink)] leading-snug pl-3 relative"
                          >
                            <span className="absolute left-0 top-[7px] h-1 w-1 bg-[var(--tir-burgundy)]"></span>
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-span-7 border-l border-[var(--tir-border)] pl-4">
                      <div className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1.5">
                        How to use it
                      </div>
                      <p className="text-[11.5px] text-[var(--tir-ink)] leading-snug">{z.howTo}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 border-t border-[var(--tir-border)] pt-4">
              <p className="text-xs text-[var(--tir-muted)] leading-relaxed">
                Train in the prescribed zones with intention, and let the data guide every session.
              </p>
            </div>
          </div>
          <BrandFooter />
        </div>
      </div>
    </div>
  );
}
